'use client';

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { GameStore, GameState, QuizResult, Badge, CategorySlug, XPNotification } from '@/types';
import { LEVEL_THRESHOLDS, calculateLevel, getLevelName } from '@/lib/gamification';

const STORAGE_KEY = 'dartdosage-game-state';

const initialGameState: GameState = {
  xp: 0,
  level: 1,
  levelName: 'Widget Trainee',
  streak: 0,
  longestStreak: 0,
  lastVisitDate: '',
  answeredQuestions: [],
  unlockedBadges: [],
  badgeUnlockDates: {},
  quizHistory: [],
  totalQuestionsRead: 0,
  categoryProgress: {} as Record<CategorySlug, number>,
};

export const useGameStore = create<GameStore>()(
  persist(
    immer((set, get) => ({
      // ─── State ──────────────────────────────────────────────────────────────
      ...initialGameState,

      // Transient (NOT persisted — excluded below)
      xpNotifications: [] as XPNotification[],
      pendingBadgeId: null,
      levelUpPending: false,
      previousLevel: 1,

      // ─── Actions ────────────────────────────────────────────────────────────

      addXP: (amount: number, reason: string) => {
        set((state) => {
          const oldLevel = state.level;
          state.xp += amount;
          const newLevel = calculateLevel(state.xp);
          state.level = newLevel;
          state.levelName = getLevelName(newLevel);

          if (newLevel > oldLevel) {
            state.levelUpPending = true;
            state.previousLevel = oldLevel;
          }

          const notification: XPNotification = {
            id: `${Date.now()}-${Math.random()}`,
            amount,
            reason,
            timestamp: Date.now(),
          };
          state.xpNotifications.push(notification);
          // Keep max 3 notifications
          if (state.xpNotifications.length > 3) {
            state.xpNotifications.shift();
          }
        });
      },

      markQuestionAnswered: (questionId: string, xpReward: number, category: CategorySlug) => {
        const { answeredQuestions, addXP } = get();
        if (answeredQuestions.includes(questionId)) return; // No double XP

        set((state) => {
          state.answeredQuestions.push(questionId);
          state.totalQuestionsRead += 1;
          state.categoryProgress[category] = (state.categoryProgress[category] ?? 0) + 1;
        });

        addXP(xpReward, `${category} question answered`);
      },

      updateStreak: () => {
        set((state) => {
          const today = new Date().toISOString().split('T')[0];
          const last = state.lastVisitDate;

          if (last === today) return; // Already visited today

          const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

          if (last === yesterday) {
            state.streak += 1;
          } else if (last !== '' && last !== yesterday) {
            state.streak = 1; // Streak broken
          } else {
            state.streak = 1; // First visit
          }

          state.longestStreak = Math.max(state.longestStreak, state.streak);
          state.lastVisitDate = today;
        });
      },

      checkAndUnlockBadges: (allBadges: Badge[]) => {
        const state = get();
        allBadges.forEach((badge) => {
          if (!state.unlockedBadges.includes(badge.id)) {
            const shouldUnlock = evaluateBadgeCondition(badge.id, state);
            if (shouldUnlock) {
              set((s) => {
                s.unlockedBadges.push(badge.id);
                s.badgeUnlockDates[badge.id] = new Date().toISOString();
                // Show badge modal for first unlocked badge
                if (!s.pendingBadgeId) {
                  s.pendingBadgeId = badge.id;
                }
              });
              // Award XP bonus for badge
              get().addXP(badge.xpBonus, `Badge unlocked: ${badge.name}`);
            }
          }
        });
      },

      addQuizResult: (result: QuizResult) => {
        set((state) => {
          state.quizHistory.unshift(result);
          // Keep last 50 quiz results
          if (state.quizHistory.length > 50) {
            state.quizHistory = state.quizHistory.slice(0, 50);
          }
        });
      },

      dismissXPNotification: (id: string) => {
        set((state) => {
          state.xpNotifications = state.xpNotifications.filter((n) => n.id !== id);
        });
      },

      dismissBadge: () => {
        set((state) => {
          state.pendingBadgeId = null;
        });
      },

      dismissLevelUp: () => {
        set((state) => {
          state.levelUpPending = false;
        });
      },

      resetProgress: () => {
        set((state) => {
          Object.assign(state, {
            ...initialGameState,
            xpNotifications: [],
            pendingBadgeId: null,
            levelUpPending: false,
            previousLevel: 1,
            categoryProgress: {} as Record<CategorySlug, number>,
          });
        });
      },

      // ─── Selectors ──────────────────────────────────────────────────────────

      getProgressToNextLevel: () => {
        const { xp, level } = get();
        const currentThreshold = LEVEL_THRESHOLDS[level - 1] ?? 0;
        const nextThreshold = LEVEL_THRESHOLDS[level];
        if (nextThreshold === undefined) return 100;
        return Math.round(((xp - currentThreshold) / (nextThreshold - currentThreshold)) * 100);
      },

      getQuestionsAnsweredInCategory: (cat: CategorySlug) => {
        return get().categoryProgress[cat] ?? 0;
      },

      isBadgeUnlocked: (badgeId: string) => {
        return get().unlockedBadges.includes(badgeId);
      },

      isQuestionAnswered: (questionId: string) => {
        return get().answeredQuestions.includes(questionId);
      },
    })),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => {
        // Safe localStorage access for SSR
        if (typeof window === 'undefined') {
          return {
            getItem: () => null,
            setItem: () => {},
            removeItem: () => {},
          };
        }
        return localStorage;
      }),
      // Do NOT persist transient UI state
      partialize: (state) => {
        const {
          xpNotifications: _xpN,
          pendingBadgeId: _pB,
          levelUpPending: _lU,
          previousLevel: _pL,
          addXP: _aX,
          markQuestionAnswered: _mQA,
          updateStreak: _uS,
          checkAndUnlockBadges: _cUB,
          addQuizResult: _aQR,
          dismissXPNotification: _dXPN,
          dismissBadge: _dB,
          dismissLevelUp: _dLU,
          resetProgress: _rP,
          getProgressToNextLevel: _gPTNL,
          getQuestionsAnsweredInCategory: _gQAIC,
          isBadgeUnlocked: _iBU,
          isQuestionAnswered: _iQA,
          ...persistent
        } = state;
        return persistent;
      },
    }
  )
);

// ─── Badge evaluation ────────────────────────────────────────────────────────
function evaluateBadgeCondition(badgeId: string, state: GameState): boolean {
  switch (badgeId) {
    case 'first-answer':
      return state.answeredQuestions.length >= 1;
    case 'on-fire':
      return state.streak >= 3;
    case 'bookworm':
      return state.totalQuestionsRead >= 50;
    case 'century':
      return state.answeredQuestions.length >= 100;
    case 'render-architect':
      return state.level >= 6;
    case 'legend':
      return state.level >= 7;
    case 'week-warrior':
      return state.streak >= 7;
    case 'monthly-hero':
      return state.streak >= 30;
    case 'star-student':
      return state.xp >= 5000;
    case 'all-rounder': {
      const cats = Object.values(state.categoryProgress).filter((v) => v > 0);
      return cats.length >= 10;
    }
    default:
      return false;
  }
}
